import { useRef, useState, useCallback } from "react";
import { Upload, Trash2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useEntityFiles,
  useUploadEntityFile,
  useDeleteEntityFile,
  formatFileSize,
  fileIcon,
  type EntityType,
} from "@/hooks/useEntityFiles";

// ── FileAttachments ──────────────────────────────────────────────────────

interface FileAttachmentsProps {
  entityType: EntityType;
  entityId: string;
  orgId: string;
}

export function FileAttachments({ entityType, entityId, orgId }: FileAttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const { data: files = [], isLoading } = useEntityFiles(entityType, entityId);
  const upload = useUploadEntityFile(entityType);
  const deleteFile = useDeleteEntityFile(entityType);

  const uploadFiles = useCallback(
    (incoming: File[]) => {
      incoming.forEach((file) => upload.mutate({ file, entityId, orgId }));
    },
    [upload, entityId, orgId]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  };

  if (isLoading) {
    return (
      <div className="h-16 flex items-center justify-center text-sm text-muted-foreground">
        Loading files…
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* File list */}
      {files.length > 0 && (
        <div className="divide-y divide-border rounded-md border border-border overflow-hidden">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-3 px-3 py-2 bg-card hover:bg-muted/40 transition-colors"
            >
              <span className="text-lg shrink-0">{fileIcon(file.mime_type)}</span>
              <a
                href={file.public_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 group"
              >
                <p className="text-sm font-medium truncate group-hover:underline">{file.filename}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  {formatFileSize(file.file_size)}
                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                </p>
              </a>
              <button
                onClick={() => {
                  if (confirm(`Delete "${file.filename}"?`)) {
                    deleteFile.mutate({ file, entityId });
                  }
                }}
                disabled={deleteFile.isPending}
                className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone — compact row when files exist, taller empty state */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "flex rounded-md border-2 border-dashed transition-colors cursor-pointer select-none",
          files.length === 0
            ? "flex-col items-center justify-center gap-1.5 py-4"
            : "flex-row items-center justify-center gap-2 py-2 px-3",
          isDraggingOver
            ? "border-primary bg-primary/5 text-primary"
            : "border-border text-muted-foreground hover:border-primary/50 hover:bg-muted/30"
        )}
      >
        <Upload className={cn("h-4 w-4 shrink-0", upload.isPending && "animate-pulse")} />
        <p className="text-xs">
          {upload.isPending
            ? "Uploading…"
            : isDraggingOver
            ? "Drop to upload"
            : files.length === 0
            ? "Drag & drop files here, or click to browse"
            : "Add more files"}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
